<script setup lang="ts">
import { UiIcon } from '@shared/ui';
import { navItems, socialItems } from './content';
import type { AppFooterProps } from './types';

const { phoneNumber = '+7 (495) 974-73-74' } = defineProps<AppFooterProps>();

const phoneNumberHref = `tel:${phoneNumber.replace(/[\s()-]/g, '')}`;

const actives = ref<string[]>([]);

const toggleNav = (title: string) => {
  if (actives.value.includes(title)) {
    actives.value = actives.value.filter(i => i !== title);
  }
  else {
    actives.value.push(title);
  }
};
</script>

<template>
  <footer
    class="w-full px-7.5 bg-white text-sm max-[576px]:px-2.5"
  >
    <div class="flex flex-col gap-8 max-w-layout mx-auto py-10 max-[576px]:pb-6">
      <div class="flex gap-6 justify-between max-[1030px]:flex-col">
        <nav
          class="grid grid-cols-3 gap-6 max-[743px]:grid-cols-1"
          itemscope
          itemtype="http://www.schema.org/SiteNavigationElement"
        >
          <div
            v-for="block in navItems"
            :key="block.title"
          >
            <button
              class="flex items-center pointer-events-none gap-1 max-[743px]:pointer-events-auto"
              @click="toggleNav(block.title)"
            >
              <span
                class="text-base font-bold"
              >
                {{ block.title }}
              </span>
              <UiIcon
                class="!text-gray-500 transition-[transform_0.3s_ease] min-[743px]:!hidden"
                :style="actives.includes(block.title) ? 'transform: rotate(180deg);' : 'transform: rotate(0deg);'"
                name="chevron-down"
              />
            </button>
            <ul
              :class="[
                'mt-2 flex flex-col gap-2 w-[15.25rem] overflow-hidden max-h-full max-[743px]:hidden max-[743px]:opacity-0 max-[743px]:max-h-0',
                actives.includes(block.title)
                  ? '!flex !max-h-full !opacity-[1]'
                  : '',
              ]"
            >
              <li
                v-for="item in block.items"
                :key="item.href"
              >
                <!-- eslint-disable vue/no-v-html -->
                <NuxtLink
                  :href="item.href"
                  :external="item.external"
                  class="transition-colors hover:text-blue-700"
                >
                  <span v-html="item.label" />
                </NuxtLink>
              </li>
            </ul>
          </div>
        </nav>

        <div class="flex flex-col items-end gap-6 max-[1230px]:items-start max-[444px]:gap-4">
          <div class="flex gap-10 max-[1230px]:flex-col max-[1230px]:gap-3  max-[1030px]:flex-row  max-[1030px]:gap-10 max-[444px]:flex-col max-[444px]:gap-3">
            <div class="flex flex-col gap-1">
              <p class="text-gray-500 text-nowrap">
                Ежедневно, с 9:00 до 19:00
              </p>
              <a
                data-allow-mismatch
                class="text-xl font-bold text-blue-600 text-nowrap"
                :href="phoneNumberHref"
              >{{ phoneNumber }}</a>
            </div>
            <div class="flex flex-col gap-1">
              <p class="text-gray-500">
                Наш адрес
              </p>
              <p
                class="text-xl font-bold text-blue-600 text-nowrap"
              >
                127083, г. Москва,<br>ул. Мишина, д. 56
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-6 items-end max-[1230px]:hidden">
            <ul class="grid grid-rows-1 grid-flow-col gap-3 max-[992px]:self-start max-[444px]:grid-rows-2">
              <li
                v-for="item in socialItems"
                :key="item.link"
                class="flex items-center justify-center"
              >
                <a
                  :href="item.link"
                  target="_blank"
                  rel="nofollow"
                  class="flex transition-transform hover:transition-[scale(1.125)]"
                >
                  <UiIcon
                    :name="item.icon"
                    svg-class="!w-8 !h-8"
                  />
                  <span class="sr-only">{{ item.alt }}</span>
                </a>
              </li>
            </ul>
            <ul class="w-full flex justify-end gap-4 max-[930px]:justify-start max-[444px]:flex-col max-[444px]:gap-2">
              <li class="flex">
                <a
                  href="https://t.me/company4dk"
                  target="_blank"
                  rel="nofollow"
                  class="flex items-center gap-2 text-sm text-blue-600 transition-colors hover:text-blue-700"
                >
                  <UiIcon
                    name="tg_color"
                    class="!w-4 !h-4 rounded-sm"
                  />
                  <span>Всё о налогах и законах</span>
                </a>
              </li>
              <li>
                <a
                  href="https://t.me/community4dk"
                  target="_blank"
                  rel="nofollow"
                  class="flex items-center gap-2 text-sm text-blue-600 transition-colors hover:text-blue-700"
                >
                  <UiIcon
                    name="tg_color"
                    class="!w-4 !h-4 rounded-sm"
                  />
                  <span>Бесплатные вебинары</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="hidden max-[1230px]:flex max-[1230px]:flex-col max-[1230px]:gap-6 max-[1230px]:self-start">
        <ul class="grid grid-rows-1 grid-flow-col gap-3 max-[992px]:self-start max-[444px]:grid-rows-2">
          <li
            v-for="item in socialItems"
            :key="item.link"
            class="flex items-center justify-center"
          >
            <a
              :href="item.link"
              target="_blank"
              rel="nofollow"
              class="flex items-center"
            >
              <UiIcon
                :name="item.icon"
                svg-class="!w-8 !h-8"
              />
              <span class="sr-only">{{ item.alt }}</span>
            </a>
          </li>
        </ul>
        <ul class="w-full flex justify-end gap-4 max-[930px]:justify-start max-[444px]:flex-col max-[444px]:gap-2">
          <li class="flex">
            <a
              href="https://t.me/company4dk"
              target="_blank"
              rel="nofollow"
              class="flex items-center gap-2 text-sm text-blue-600 transition-colors hover:text-blue-700"
            >
              <UiIcon
                name="tg_color"
                class="!w-4 !h-4 rounded-sm"
              />
              <span>Всё о налогах и законах</span>
            </a>
          </li>
          <li>
            <a
              href="https://t.me/community4dk"
              target="_blank"
              rel="nofollow"
              class="flex items-center gap-2 text-sm text-blue-600 transition-colors hover:text-blue-700"
            >
              <UiIcon
                name="tg_color"
                class="!w-4 !h-4 rounded-sm"
              />
              <span>Бесплатные вебинары</span>
            </a>
          </li>
        </ul>
      </div>

      <p class="text-xs text-gray-500">
        © 1993—{{ new Date().getFullYear() }} Первый Дом Консалтинга «Что делать Консалт». Все права защищены.
      </p>
    </div>
  </footer>
</template>
